/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { Close, DeleteOutline, SelectAll } from '@mui/icons-material'
import { Chip, IconButton, Stack, Tooltip } from '@mui/material'

import useT from '@/i18n/useT'
import {
  DetectionBox,
  DetectionClass,
  DetectionTagOption,
  getDetectionClassLabels,
  getDetectionClassColor,
} from './detectionUtils'

type DetectionSelectionControlsProps = {
  boxes: DetectionBox[]
  selectedBoxes: DetectionBox[]
  canEdit: boolean
  removingBoxes: boolean
  canApplyCurrentLabel: boolean
  canApplyActiveTag: boolean
  activeClass: DetectionClass
  activeTag: DetectionTagOption
  onApplyCurrentLabel: () => void
  onApplyActiveTag: () => void
  onDeleteSelected: () => void
  onSelectedBoxIdsChange: (boxIds: string[]) => void
}

function DetectionSelectionControls({
  boxes,
  selectedBoxes,
  canEdit,
  removingBoxes,
  canApplyCurrentLabel,
  canApplyActiveTag,
  activeClass,
  activeTag,
  onApplyCurrentLabel,
  onApplyActiveTag,
  onDeleteSelected,
  onSelectedBoxIdsChange,
}: DetectionSelectionControlsProps) {
  const { t } = useT()
  const detectionClassLabels = getDetectionClassLabels(t)
  const activeClassColor = getDetectionClassColor(activeClass, activeTag.name)
  const activeTagColor = getDetectionClassColor('PRODUCT', activeTag.name)
  const hasSelectedBoxes = selectedBoxes.length > 0

  if (!hasSelectedBoxes) {
    return (
      <Tooltip
        title={t(
          'detectionSelectionControls.selectAll.tooltip',
          'Select all boxes',
        )}
      >
        <IconButton
          size="small"
          aria-label={t(
            'detectionSelectionControls.selectAll.ariaLabel',
            'Select all boxes',
          )}
          onClick={() => onSelectedBoxIdsChange(boxes.map(box => box.id))}
          disabled={boxes.length === 0}
        >
          <SelectAll fontSize="small" />
        </IconButton>
      </Tooltip>
    )
  }

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
    >
      <Chip
        size="small"
        label={t('detectionSelectionControls.selectedCount', {
          count: selectedBoxes.length,
          defaultValue:
            selectedBoxes.length === 1
              ? '1 selected'
              : `${selectedBoxes.length} selected`,
        })}
        variant="outlined"
      />
      <Tooltip
        title={t(
          'detectionSelectionControls.selectAll.tooltip',
          'Select all boxes',
        )}
      >
        <IconButton
          size="small"
          aria-label={t(
            'detectionSelectionControls.selectAll.ariaLabel',
            'Select all boxes',
          )}
          onClick={() => onSelectedBoxIdsChange(boxes.map(box => box.id))}
          disabled={selectedBoxes.length === boxes.length}
        >
          <SelectAll fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip
        title={t(
          'detectionSelectionControls.deselectAll.tooltip',
          'Deselect all',
        )}
      >
        <IconButton
          size="small"
          aria-label={t(
            'detectionSelectionControls.deselectAll.ariaLabel',
            'Deselect all selected boxes',
          )}
          onClick={() => onSelectedBoxIdsChange([])}
        >
          <Close fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip
        title={t(
          'detectionSelectionControls.deleteSelected.tooltip',
          'Delete selected',
        )}
      >
        <span>
          <IconButton
            size="small"
            aria-label={t(
              'detectionSelectionControls.deleteSelected.ariaLabel',
              'Delete selected boxes',
            )}
            color="error"
            onClick={onDeleteSelected}
            disabled={removingBoxes || !canEdit}
          >
            <DeleteOutline fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      {canEdit && canApplyCurrentLabel && (
        <Tooltip
          title={t('detectionSelectionControls.applyLabel.tooltip', {
            label: detectionClassLabels[activeClass],
            defaultValue: `Apply ${detectionClassLabels[activeClass]} to selected boxes`,
          })}
        >
          <span>
            <Chip
              clickable={canEdit}
              size="small"
              label={detectionClassLabels[activeClass]}
              onClick={canEdit ? onApplyCurrentLabel : undefined}
              sx={{
                borderColor: activeClassColor,
                color: activeClassColor,
                backgroundColor: `${activeClassColor}14`,
              }}
              variant="outlined"
            />
          </span>
        </Tooltip>
      )}
      {canEdit && canApplyActiveTag && (
        <Tooltip
          title={t('detectionSelectionControls.applyTag.tooltip', {
            tag: activeTag.name,
            defaultValue: `Apply tag ${activeTag.name} to selected product boxes`,
          })}
        >
          <span>
            <Chip
              clickable={canEdit}
              size="small"
              label={activeTag.name}
              onClick={canEdit ? onApplyActiveTag : undefined}
              sx={{
                borderColor: activeTagColor,
                color: activeTagColor,
                backgroundColor: `${activeTagColor}14`,
              }}
              variant="outlined"
            />
          </span>
        </Tooltip>
      )}
    </Stack>
  )
}

export default DetectionSelectionControls
